import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Badge,
  Alert,
  Spinner,
  Row,
  Col,
  Button,
  ListGroup,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { adminService } from "../../services/adminService";
import ReportFilters, { today, monthAgo } from "./ReportFilters";
import { formatShowDateIST, formatShowDateTimeIST } from "../../utils/dateTimeIST";
import toast from "react-hot-toast";

const fmt = (v: any) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtDate = (v: any) => formatShowDateIST(v);

const PayoutSummaryReport: React.FC = () => {
  const [filters, setFilters] = useState({
    dateFrom: monthAgo,
    dateTo: today,
    cinemaId: "",
  });
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cinemas, setCinemas] = useState<Array<{ id: string; name: string }>>(
    [],
  );

  // Daily cron-generated payout files
  const [dailyFiles, setDailyFiles] = useState<any[]>([]);
  const [dailyFilesLoading, setDailyFilesLoading] = useState(false);
  const [downloadingDate, setDownloadingDate] = useState<string | null>(null);

  useEffect(() => {
    adminService
      .getCinemas()
      .then((r: any) => {
        const list = r?.data || [];
        setCinemas(
          list.map((c: any) => ({ id: c.vistacinemaid || c.id, name: c.name })),
        );
      })
      .catch(() => setCinemas([]));
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const r: any = await adminService.getPayoutSummary(filters);
      setRows(r?.data || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load payout summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = fetchData;

  const onReset = () => {
    setFilters({
      dateFrom: monthAgo,
      dateTo: today,
      cinemaId: "",
    });
    setTimeout(fetchData, 0);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await adminService.exportPayoutSummary(filters);
      toast.success("Excel downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // ------------------------------------------------------------------
  // Daily cron-generated payout files (downloadable archive)
  // ------------------------------------------------------------------
  const loadDailyFiles = async () => {
    setDailyFilesLoading(true);
    try {
      const r: any = await adminService.getDailyPayoutFiles();
      setDailyFiles(r?.data || []);
    } catch (e: any) {
      setDailyFiles([]);
    } finally {
      setDailyFilesLoading(false);
    }
  };

  useEffect(() => {
    loadDailyFiles();
  }, []);

  const onDownloadDailyFile = async (date: string) => {
    setDownloadingDate(date);
    try {
      await adminService.downloadDailyPayoutFile(date);
      toast.success(`payout-summary-${date}.xlsx downloaded`);
    } catch (e: any) {
      toast.error(e?.message || "Download failed");
    } finally {
      setDownloadingDate(null);
    }
  };

  const fmtSize = (b: number) => {
    if (!b) return "0 B";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
  };

  const totals = rows.reduce(
    (acc, r) => ({
      totalBookings: acc.totalBookings + Number(r.totalBookings || 0),
      ticketQty: acc.ticketQty + Number(r.ticketQty || 0),
      grossTickets: acc.grossTickets + Number(r.grossTickets || 0),
      fnbAmount: acc.fnbAmount + Number(r.fnbAmount || 0),
      gatewayRefunds: acc.gatewayRefunds + Number(r.gatewayRefunds || 0),
      mrRefund: acc.mrRefund + Number(r.mrRefund || 0),
      gatewayFee: acc.gatewayFee + Number(r.gatewayFee || 0),
      netCollected: acc.netCollected + Number(r.netCollected || 0),
      refundQty: acc.refundQty + Number(r.refundQty || 0),
      netPayable: acc.netPayable + Number(r.netPayable || 0),
    }),
    {
      totalBookings: 0,
      ticketQty: 0,
      grossTickets: 0,
      fnbAmount: 0,
      gatewayRefunds: 0,
      mrRefund: 0,
      gatewayFee: 0,
      netCollected: 0,
      refundQty: 0,
      netPayable: 0,
    },
  );

  return (
    <>
      <Helmet>
        <title>Cinema Payout Summary - MyCinemas Admin</title>
      </Helmet>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Cinema Payout Summary</h2>
          <small className="text-muted">
            One row per cinema per day. Auto-generated daily at 6 AM IST and
            emailed to cinema managers + super admin.
          </small>
        </div>
        <Badge bg="success" className="p-2">
          {rows.length} rows
        </Badge>
      </div>

      <ReportFilters
        {...filters}
        cinemas={cinemas}
        onChange={(p) => setFilters((prev) => ({ ...prev, ...p }))}
        onApply={onApply}
        onReset={onReset}
        onExport={onExport}
        loading={loading}
        exporting={exporting}
      />

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-3 g-2">
        <Col md={3}>
          <Card body className="text-center bg-light">
            <small className="text-muted">Total Net Collected</small>
            <h5 className="mb-0">{fmt(totals.netCollected)}</h5>
          </Card>
        </Col>
        <Col md={3}>
          <Card body className="text-center bg-light">
            <small className="text-muted">Total Gateway Fee</small>
            <h5 className="mb-0 text-danger">{fmt(totals.gatewayFee)}</h5>
          </Card>
        </Col>
        <Col md={3}>
          <Card body className="text-center bg-light">
            <small className="text-muted">Total Net Payable</small>
            <h5 className="mb-0">{fmt(totals.netPayable)}</h5>
          </Card>
        </Col>
        <Col md={3}>
          <Card body className="text-center bg-light">
            <small className="text-muted">Total Bookings</small>
            <h5 className="mb-0">{totals.totalBookings}</h5>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="mb-0">
            <thead className="table-light">
              <tr>
                <th>Cinema</th>
                <th>Date</th>
                <th className="text-end">Bookings</th>
                <th className="text-end">Ticket Qty</th>
                <th className="text-end">Gross Tickets</th>
                <th className="text-end">F&amp;B</th>
                <th className="text-end">Gateway Refunds</th>
                <th className="text-end">MR Refund</th>
                <th className="text-end">Gateway Fee</th>
                <th className="text-end">Net Collected</th>
                <th className="text-end">Refund Qty</th>
                <th className="text-end">Net Payable</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-4">
                    <Spinner size="sm" /> Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-4 text-muted">
                    No payout data found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: any, i: number) => (
                  <tr key={`${r.cinemaId}-${r.date}-${i}`}>
                    <td className="small">{r.cinema}</td>
                    <td className="small">{fmtDate(r.date)}</td>
                    <td className="text-end small">{r.totalBookings}</td>
                    <td className="text-end small">{r.ticketQty}</td>
                    <td className="text-end small">{fmt(r.grossTickets)}</td>
                    <td className="text-end small">{fmt(r.fnbAmount)}</td>
                    <td className="text-end small text-danger">
                      {fmt(r.gatewayRefunds)}
                    </td>
                    <td className="text-end small text-danger">
                      {fmt(r.mrRefund)}
                    </td>
                    <td className="text-end small text-danger">
                      {fmt(r.gatewayFee)}
                    </td>
                    <td className="text-end small fw-bold">
                      {fmt(r.netCollected)}
                    </td>
                    <td className="text-end small">{r.refundQty}</td>
                    <td className="text-end small fw-bold text-success">
                      {fmt(r.netPayable)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="table-light">
                <tr className="fw-bold">
                  <td>TOTAL</td>
                  <td>-</td>
                  <td className="text-end">{totals.totalBookings}</td>
                  <td className="text-end">{totals.ticketQty}</td>
                  <td className="text-end">{fmt(totals.grossTickets)}</td>
                  <td className="text-end">{fmt(totals.fnbAmount)}</td>
                  <td className="text-end">{fmt(totals.gatewayRefunds)}</td>
                  <td className="text-end">{fmt(totals.mrRefund)}</td>
                  <td className="text-end">{fmt(totals.gatewayFee)}</td>
                  <td className="text-end">{fmt(totals.netCollected)}</td>
                  <td className="text-end">{totals.refundQty}</td>
                  <td className="text-end">{fmt(totals.netPayable)}</td>
                </tr>
              </tfoot>
            )}
          </Table>
        </Card.Body>
      </Card>

      {/* Daily Generated Reports — download from server */}
      <Card className="mt-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">
              <i className="bi-calendar-check me-2"></i>
              Daily Generated Reports
            </h5>
            <small className="text-muted">
              Auto-generated XLSX files saved by the 6 AM IST daily cron.
              Click a date to download the original file from the server.
            </small>
          </div>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={loadDailyFiles}
            disabled={dailyFilesLoading}
          >
            {dailyFilesLoading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <i className="bi-arrow-clockwise me-1"></i>
                Refresh
              </>
            )}
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {dailyFilesLoading && dailyFiles.length === 0 ? (
            <div className="text-center py-4">
              <Spinner size="sm" /> Loading…
            </div>
          ) : dailyFiles.length === 0 ? (
            <Alert
              variant="info"
              className="m-3 mb-3"
            >
              No daily reports generated yet. The first file will appear here
              after the 6 AM IST cron runs (one file per day, named by date).
              The cron-generated file is independent of the "Export" button
              above — manual export streams a fresh XLSX straight to your
              browser without touching these files.
            </Alert>
          ) : (
            <ListGroup variant="flush">
              {dailyFiles.map((f: any) => (
                <ListGroup.Item
                  key={f.date}
                  className="d-flex justify-content-between align-items-center"
                >
                  <div>
                    <strong>
                      <i className="bi-file-earmark-spreadsheet me-2 text-success"></i>
                      payout-summary-{f.date}.xlsx
                    </strong>
                    <div className="small text-muted">
                      Generated: {formatShowDateTimeIST(f.generatedAt)} ·{" "}
                      Size: {fmtSize(f.sizeBytes)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => onDownloadDailyFile(f.date)}
                    disabled={downloadingDate === f.date}
                  >
                    {downloadingDate === f.date ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="bi-download me-1"></i>
                        Download
                      </>
                    )}
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default PayoutSummaryReport;
